import React, { useEffect, useState, useMemo } from 'react';
import {
  FiCheckCircle,
  FiClock,
  FiLoader,
  FiTrendingUp,
  FiActivity
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { assessmentsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getCountsFromAssessments } from '../../utils/assessmentStatus';
import { useAccessCheck } from '../../hooks/useAccessCheck';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// pieData will be derived from API counts inside the component

// const recentActivity = [
//   { id: 1, action: 'Reviewed', entity: 'Ubiquity - NIST 800-53', user: 'You', time: '2 hours ago', status: 'Completed' },
//   { id: 2, action: 'Commented on', entity: 'BlackBox - SIG Lite', user: 'You', time: '4 hours ago', status: 'On Hold' },
//   { id: 3, action: 'Approved', entity: 'Bitsight - SIG Lite', user: 'You', time: '1 day ago', status: 'Completed' },
//   { id: 4, action: 'Reviewed', entity: 'Exbeam - SIG Lite', user: 'You', time: '2 days ago', status: 'Processing' },
//   { id: 5, action: 'Started review', entity: 'Valorant - SIG Lite', user: 'You', time: '3 days ago', status: 'Processing' },
// ];

// CARD_DATA will be derived from API counts inside the component

// const statusColors = {
//   'Completed': 'bg-[#0077b5]/15 text-[#0077b5]',
//   'Processing': 'bg-[#F47F2E]/15 text-[#F47F2E]',
//   'On Hold': 'bg-[#F47F2E]/15 text-[#F47F2E]',
//   'Pending': 'bg-[#0077b5]/15 text-[#0077b5]',
// };

const ReviewerDashboard = () => {
    const { user } = useAuth();
  const [counts, setCounts] = useState({ completed: 0, processing: 0, pending: 0 });
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

    useAccessCheck('cud_dashboard', '/reviewer/assessments');

  useEffect(() => {
    let mounted = true;
    async function loadCounts() {
      try {
        const data = await assessmentsApi.CountInfo(user?.id);
        if (!mounted) return;
        if (Array.isArray(data)) {
          // Only include assessments with at least one questionnaire (matches UserDashboard logic)
          const filtered = data.filter(a => Array.isArray(a.questionnaires) && a.questionnaires.length > 0);
          setAssessments(filtered);
          const { completed, processing, pending } = getCountsFromAssessments(filtered);
          setCounts({ completed, processing, pending });
        } else {
          setAssessments([]);
          setCounts({ completed: 0, processing: 0, pending: 0 });
        }
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load counts');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (user?.id) loadCounts();
    return () => { mounted = false; };
  }, [user?.id]);

  // Build monthly trend data from API assessments
  const trendData = useMemo(() => {
    const monthsToShow = 7;
    const now = new Date();
    const window = [];
    for (let i = monthsToShow - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      window.push({ key: `${d.getFullYear()}-${d.getMonth()}`, month: MONTHS[d.getMonth()], completed: 0, processing: 0, pending: 0 });
    }

    if (!Array.isArray(assessments) || assessments.length === 0) return window;

    const completedCounts = {};
    const processingCounts = {};
    const pendingCounts = {};

      const isCompleted = (a) => {
      if (!Array.isArray(a.questionnaires) || a.questionnaires.length === 0) return false;
      return a.questionnaires.every(q => (q.totalQuestion ?? 0) > 0 && (q.answered ?? 0) >= (q.totalQuestion ?? 0));
    };
    const isProcessing = (a) => {
      if (!Array.isArray(a.questionnaires) || a.questionnaires.length === 0) return false;
      if (isCompleted(a)) return false;
      return a.questionnaires.some(q => (q.answered ?? 0) > 0);
    };

    assessments.forEach(a => {
      if (!Array.isArray(a.questionnaires) || a.questionnaires.length === 0) return;

      // Derive date as the latest lastUpdated among questionnaires
      let latest = null;
      a.questionnaires.forEach(q => {
        if (!q?.lastUpdated) return;
        const d = new Date(q.lastUpdated);
        if (isNaN(d)) return;
        if (!latest || d > latest) latest = d;
      });
      if (!latest) return;

      const key = `${latest.getFullYear()}-${latest.getMonth()}`;
      if (isCompleted(a)) {
        completedCounts[key] = (completedCounts[key] || 0) + 1;
      } else if (isProcessing(a)) {
        processingCounts[key] = (processingCounts[key] || 0) + 1;
      } else {
        pendingCounts[key] = (pendingCounts[key] || 0) + 1;
      }
    });

    return window.map(w => ({
      month: w.month,
      completed: completedCounts[w.key] || 0,
      processing: processingCounts[w.key] || 0,
      pending: pendingCounts[w.key] || 0,
    }));
  }, [assessments]);

  const CARD_DATA = [
    {
      title: 'Completed',
      value: counts.completed,
      subtitle: 'Reviews finalized',
      icon: FiCheckCircle,
      gradient: 'from-[#0077b5] to-[#006599]',
      lightBg: 'bg-[#0077b5]/10',
      lightText: 'text-[#0077b5]',
    },
    {
      title: 'Processing',
      value: counts.processing,
      subtitle: 'Currently in review',
      icon: FiLoader,
      gradient: 'from-[#F47F2E] to-[#d96a1f]',
      lightBg: 'bg-[#F47F2E]/10',
      lightText: 'text-[#F47F2E]',
    },
    {
      title: 'Pending',
      value: counts.pending,
      subtitle: 'Awaiting review',
      icon: FiClock,
      gradient: 'from-[#0077b5] to-[#006599]',
      lightBg: 'bg-[#0077b5]/10',
      lightText: 'text-[#0077b5]',
    },
  ];

  const pieData = [
    { name: 'Completed', value: counts.completed, color: '#0077b5' },
    { name: 'Processing', value: counts.processing, color: '#F47F2E' },
    { name: 'Pending', value: counts.pending, color: '#0077b5' },
  ];

  // Custom label for PieChart: hide 0% labels
  const pieLabel = ({ name, percent }) => {
    if (percent === 0) return '';
    return `${name} ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="space-y-6">
          <div>
            <div className="w-64 h-7 bg-[#e6eef5] rounded animate-pulse mb-2" />
            <div className="w-96 h-4 bg-[#e6eef5] rounded animate-pulse" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0,1,2].map(i => (
              <div key={i} className=" p-6 bg-white cardStyle">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-[#e6eef5] rounded-xl animate-pulse" />
                  <div className="w-8 h-4 bg-[#e6eef5] rounded animate-pulse" />
                </div>
                <div className="w-24 h-8 bg-[#e6eef5] rounded animate-pulse mb-3" />
                <div className="w-32 h-3 bg-[#e6eef5] rounded animate-pulse" />
                <div className="w-40 h-2 bg-[#e6eef5] rounded animate-pulse mt-2" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 p-6 bg-white cardStyle">
              <div className="flex items-center justify-between mb-4">
                <div className="w-48 h-5 bg-[#e6eef5] rounded animate-pulse" />
                <div className="flex items-center gap-2">
                  <div className="w-12 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  <div className="w-12 h-3 bg-[#e6eef5] rounded animate-pulse" />
                </div>
              </div>

              <div className="relative w-full h-64 mt-4">
                {/* Y-axis ticks */}
                <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between py-4">
                  {[0,1,2,3,4].map(i => (
                    <div key={i} className="w-6 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  ))}
                </div>

                {/* Chart grid area */}
                <div className="absolute left-10 right-0 top-0 bottom-8">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="absolute left-0 right-0" style={{ top: `${(i / 5) * 100}%` }}>
                      <div className="h-px bg-[#e6eef5]" />
                    </div>
                  ))}

                  {/* Area chart SVG skeleton */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <svg viewBox="0 0 700 200" preserveAspectRatio="none" className="w-full h-full">
                      <defs>
                        <linearGradient id="skeletonArea" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#e6eef5" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#e6eef5" stopOpacity="0.45" />
                        </linearGradient>
                      </defs>
                      <path d="M0,150 C60,120 120,100 180,90 C240,80 300,70 360,60 C420,55 480,60 540,50 C600,40 660,30 700,35 L700,200 L0,200 Z"
                        fill="url(#skeletonArea)" stroke="#e6eef5" strokeWidth="2" className="animate-pulse" />
                      <path d="M0,150 C60,120 120,100 180,90 C240,80 300,70 360,60 C420,55 480,60 540,50 C600,40 660,30 700,35"
                        fill="none" stroke="#d1e6f3" strokeWidth="2" className="animate-pulse" />
                    </svg>
                  </div>

                  {/* X-axis month labels */}
                  <div className="absolute left-2 right-2 bottom-0 flex items-center justify-between mt-2">
                    {Array.from({ length: 7 }).map((_, idx) => (
                      <div key={idx} className="w-8 h-3 bg-[#e6eef5] rounded animate-pulse" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className=" p-6 bg-white cardStyle">
              <div className="mb-4">
                <div className="w-36 h-5 bg-[#e6eef5] rounded animate-pulse" />
                <div className="w-28 h-3 bg-[#e6eef5] rounded animate-pulse mt-2" />
              </div>

              <div className="flex items-center justify-center">
                <div className="w-40 h-40 relative flex items-center justify-center">
                  <div className="w-40 h-40 rounded-full bg-[#e6eef5] animate-pulse" />
                  <div className="w-20 h-20 rounded-full bg-white absolute" />
                </div>
              </div>

              <div className="mt-4 w-full flex items-center justify-center gap-2">
                {[0,1,2].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-[#e6eef5] animate-pulse" />
                    <div className="w-12 h-3 bg-[#e6eef5] rounded animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    {
      !loading && (
        <>
              {/* Page Title */}
      <div>

        <p className={`text-sm mt-1 text-[#919191]`}>
          Overview of your review activities and assessment status
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CARD_DATA.map((card) => {
          const IconEl = card.icon;
          return (
            <div
              key={card.title}
              className={` p-6 ${
                'bg-white cardStyle'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center`}>
                  <IconEl className="text-white text-xl" />
                </div>
                <FiTrendingUp className={`text-lg text-[#0077b5]`} />
              </div>
              <p className={`text-3xl font-bold text-[#53545a]`}>{loading ? '-' : card.value}</p>
              <p className={`text-sm font-medium mt-1 text-[#919191]`}>{card.title}</p>
              <p className={`text-xs mt-0.5 text-[#919191]`}>{card.subtitle}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Assessment Trends - Area Chart */}
        <div className={`lg:col-span-2 p-6 bg-white cardStyle`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessment Trends</h2>
              <p className={`text-xs mt-1 text-[#919191]`}>Monthly review overview</p>
            </div>
            <FiActivity className={`text-lg text-[#919191]`} />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0077b5" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0077b5" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorProcessing" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F47F2E" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F47F2E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0077b5" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0077b5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" />
              <XAxis dataKey="month" stroke="#919191" tick={{ fontSize: 12 }} />
              <YAxis stroke="#919191" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: `1px solid #919191`,
                  borderRadius: '12px',
                  color: '#53545a',
                }}
              />
              <Area type="monotone" dataKey="completed" stroke="#0077b5" fill="url(#colorCompleted)" strokeWidth={2} />
              <Area type="monotone" dataKey="processing" stroke="#F47F2E" fill="url(#colorProcessing)" strokeWidth={2} />
              <Area type="monotone" dataKey="pending" stroke="#0077b5" fill="url(#colorPending)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className={`p-6 bg-white cardStyle`}>
          <div className="mb-4">
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Status Distribution</h2>
            <p className={`text-xs mt-1 text-[#919191]`}>Assessment breakdown</p>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={4}
                  dataKey="value"
                  label={pieLabel}
                >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" height={36} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

        </>
      )
    }
      {/* Recent Activity */}
      {/* <div className={`rounded-2xl p-6 bg-white border border-[#919191] shadow-sm`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Recent Activity</h2>
            <p className={`text-xs mt-1 text-[#919191]`}>Your latest review actions</p>
          </div>
        </div>
        <div className="space-y-4">
          {recentActivity.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                'bg-[#919191]/10 hover:bg-[#919191]/20'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  item.status === 'Completed' ? 'bg-[#0077b5]/15' : item.status === 'Processing' ? 'bg-[#F47F2E]/15' : 'bg-[#F47F2E]/15'
                }`}>
                  {item.status === 'Completed' ? (
                    <FiCheckCircle className="text-[#0077b5]" />
                  ) : item.status === 'Processing' ? (
                    <FiLoader className="text-[#F47F2E]" />
                  ) : (
                    <FiClock className="text-[#F47F2E]" />
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium text-[#53545a]`}>
                    {item.action} <span className={`text-[#0077b5]`}>{item.entity}</span>
                  </p>
                  <p className={`text-xs text-[#919191]`}>{item.user} · {item.time}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[item.status] || 'bg-[#919191]/20 text-[#919191]'}`}>
                {item.status}
              </span>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default ReviewerDashboard;
